/* vms-configurator-3d.js - the configurator 3D view (Phase 4, 2026-09-15).
 *
 * WHAT: a "3D view" toggle in step 4. When on it draws, BELOW the 2D elevation
 * scene (ruling 9.4, the 2D scene is never removed), a three.js assembly of the
 * build from the Class 3B part GLBs, snapping anchor nodes plate -> pipe(s) with
 * couplers between -> head, the TV as a translucent box with an outline and the
 * callouts as real HTML text.
 * There is NO simulated ceiling (removed by owner instruction 2026-09-16, which
 * supersedes spec 4.6); the dimension overlay from the y = 0 contact line down to
 * the VESA center is how the ceiling is communicated now. The canvas height grows
 * with the build and is capped so the callout strip stays visible below it, with a
 * mobile align-on-open that puts the canvas top at the viewport top (owner 2026-09-16).
 * A build whose parts lack a model file reports "not available" and draws nothing.
 *
 * HOW IT LOADS: a PLAIN classic script (deferred <script src>). three.js is NOT
 * loaded at page load; the ~790 KB bundle (assets/vms-three-bundle.js) is pulled
 * by a dynamic import() on the FIRST toggle only (see importBundle for why the
 * import is hidden from Shopify's CDN minifier).
 *
 * PUBLIC API (window.VMSConfigurator3D): computeChain(build, pool, opts) PURE and
 * testable; init(container); show(); hide(); dispose(); state() (a snapshot for
 * the smoke: loaded parts, triangles, last chain, first-frame time, gen).
 *
 * [RULE] ES2019 or older here (no optional chaining, no nullish coalescing),
 *   EXCEPT dynamic import(), which is required and allowed.
 * [RULE] ASCII only; no em dash or en dash anywhere (ranges written "X to Y").
 * [RULE] Every customer-facing string is a section setting, read off the data-
 *   attributes on .vmscfg-3d-wrap (a script cannot call asset_url).
 * [RULE] Progressive enhancement: every entry point guards on its globals, so a
 *   missing bundle, a missing VMSConfiguratorPage or a blocked CDN costs the 3D
 *   view and nothing else. The configurator still solves, prices and adds to cart.
 */
(function () {
  'use strict';

  // ---- constants (inches; the anchor math is all in inches, +Y up) ----------
  var IN_TO_M = 0.0254;          // GLBs are in metres; the chain is in inches
  var COUPLER_ADD = 1.5;         // 11AC001 net add per coupler (vms-configurator.js:208)
  var TV_DEPTH_IN = 2.5;         // documented constant; the elevation model has no TV depth (spec 4.5)
  var ASSERT_TOL_IN = 0.01;      // the 0.01 in bar for the anchor assertions (spec 4.3, 4.4)
  var SCENE_TRI_CAP = 100000;    // assembled-scene triangle cap (spec 4.11 / 6.11 b)
  // Per-class triangle caps from the Phase 3 part contract (RENDERER-SPEC 5.3),
  // used ONLY for computeChain's static budget estimate. The REAL triangle count
  // is summed off the loaded meshes at render time and exposed in state().
  var TRI_CAP = { ceiling_plate: 20000, mounting_head: 30000, coupler: 4000, pipe_fixed: 3000, pipe_tele: 6000 };

  function num(v) { return (v === null || v === undefined || v === '' || isNaN(Number(v))) ? null : Number(v); }
  function hasModel(v) { return typeof v === 'string' && v.length > 0; }
  function near(a, b, tol) { return Math.abs(a - b) <= tol; }

  // parseTvOpacity: the data-tv-opacity attribute is a PERCENT string ('30');
  // return the 0 to 1 fraction, clamped, with a 0.30 default for null/''/NaN.
  // PURE and exposed on the public API for the Node unit test (spec 8.1).
  function parseTvOpacity(attrValue) {
    if (attrValue === null || attrValue === undefined || attrValue === '') { return 0.30; }
    var n = Number(attrValue);
    if (isNaN(n)) { return 0.30; }
    n = n / 100;
    if (n < 0) { n = 0; }
    if (n > 1) { n = 1; }
    return n;
  }

  // =========================================================================
  // computeChain: PURE. build = getBuild() snapshot, pool = sku -> part map.
  // Output: placements[] ({ sku, role, model_glb, y_top_in, y_bottom_in, and
  // for the telescoping piece tele_solved_in / length_min_in / length_max_in }),
  // plus finished_in, vesa_y_in, tele_solved_in, clamped, available,
  // missing_models[], coupler_mismatch, tris_estimate, tris_budget_ok,
  // finished_delta_in, card_index, reason.
  // Chain rules (spec 3), all inches, ceiling contact plane at y = 0: plate
  // y_top 0, y_bottom = -plate.adds_length_in; pipes in the card's order with a
  // coupler (1.5 in) between every two pieces; telescoping length solved from
  // the committed target; head vesa_y = last y_bottom - head.adds_length_in;
  // finished = -vesa_y.
  // [UX R2 A2 2026-09-22] Owner: "the adjustable extensions can only be adjusted
  // in 1 inch increments. So that would have to be 54 or 55 inches. The one I saw
  // was an EXT3260 adjusted to 59.5 would be either 59 or 60 but nothing in
  // between." The continuous telescoping solve is therefore snapped to the pipe's
  // own whole inch grid (length_min plus a whole number of inches) BEFORE the
  // clamp, so finished_in is the length the snapped position really gives.
  // =========================================================================
  function computeChain(build, pool, opts) {
    opts = opts || {};
    var out = {
      placements: [], finished_in: null, vesa_y_in: null, tele_solved_in: null,
      clamped: false, available: false, missing_models: [], coupler_mismatch: false,
      tris_estimate: 0, tris_budget_ok: true, finished_delta_in: null,
      card_index: null, reason: null
    };
    if (!build || !pool) { out.reason = 'no-build-or-pool'; return out; }
    var builds = build.builds || [];
    if (!builds.length) { out.reason = 'no-builds'; return out; }

    // Chosen card. The spec says "the card the engine shows as chosen", but no
    // such marker exists in getBuild() (FACTS 1: mcpBuild has no chosen/selected
    // field), so we default to the first card, with an opts.cardIndex override
    // for the smoke test and any future engine "chosen" flag.
    var idx = (opts.cardIndex !== null && opts.cardIndex !== undefined && builds[opts.cardIndex]) ? opts.cardIndex : 0;
    var card = builds[idx];
    out.card_index = idx;

    var plate = build.plate || {};
    var head = build.head || {};
    var plateSku = plate.sku || null;
    var headSku = head.sku || null;
    var plateAdds = num(plate.adds_length_in) || 0;
    var headAdds = num(head.adds_length_in) || 0;
    var target = num(build.length_in);

    // Classify the card's parts against the pool. base.lines put the plate and
    // head first, then pipe lines aggregated per SKU (quantity), then one coupler
    // line (FACTS 1). We rebuild the ordered mast pieces from the pipe lines and
    // find the coupler SKU; the telescoping piece is the pipe whose pool entry
    // carries length_min and length_max (spec 3 rule 2).
    var parts = card.parts || [];
    var pieces = [];
    var couplerSku = null;
    var i, p, pd, q;
    for (i = 0; i < parts.length; i++) {
      p = parts[i];
      pd = pool[p.sku];
      if (!pd) { continue; }
      var ct = pd.component_type;
      if (ct === 'pipe' || ct === 'nipple') { // a close nipple is a fixed pipe
        q = num(p.quantity) || 1;
        var lmin = num(pd.length_min), lmax = num(pd.length_max), nom = num(pd.nominal_length);
        var isTele = (lmin !== null && lmax !== null);
        for (var k = 0; k < q; k++) {
          pieces.push({ sku: p.sku, tele: isTele, nominal: nom, length_min: lmin, length_max: lmax, model_glb: pd.model_glb || null });
        }
      } else if (ct === 'coupler') {
        couplerSku = p.sku;
      }
    }

    // Couplers physically sit BETWEEN pieces, so there are pieces-1 of them. The
    // card also reports a couplers count; if the two disagree we flag it and go
    // with pieces-1 (spec 3 rule 3).
    var physCouplers = Math.max(0, pieces.length - 1);
    if (card.couplers !== null && card.couplers !== undefined && Number(card.couplers) !== physCouplers) {
      out.coupler_mismatch = true;
    }

    // Solved telescoping length (spec 3 rule 4). getBuild() exposes no solved
    // field (FACTS 1), so we subtract: target - plate - head - couplers*1.5 -
    // sum(fixed nominal). Clamp to [length_min, length_max]; the engine's own
    // bracket rule means an in-range target never clamps.
    var telePiece = null, fixedSum = 0;
    for (i = 0; i < pieces.length; i++) {
      if (pieces[i].tele) { telePiece = pieces[i]; }
      else { fixedSum += (pieces[i].nominal || 0); }
    }
    var solved = null;
    if (telePiece && target !== null) {
      solved = target - plateAdds - headAdds - physCouplers * COUPLER_ADD - fixedSum;
      // [UX R2 A2 2026-09-22] Snap to the pipe's whole inch grid measured from its
      // own minimum, before the clamp: the adjustable extensions only adjust in 1
      // in increments, so an EXT3260 at 59.5 is either 59 or 60 and nothing in
      // between. Math.round is the tie rule the owner gave by that example.
      solved = telePiece.length_min + Math.round(solved - telePiece.length_min);
      if (solved < telePiece.length_min) { solved = telePiece.length_min; out.clamped = true; }
      else if (solved > telePiece.length_max) { solved = telePiece.length_max; out.clamped = true; }
      out.tele_solved_in = solved;
    }

    // Walk the chain top to bottom.
    var placements = [];
    var cursor = 0;
    placements.push({ sku: plateSku, role: 'plate', model_glb: (pool[plateSku] && pool[plateSku].model_glb) || null, y_top_in: 0, y_bottom_in: -plateAdds });
    cursor = -plateAdds;
    for (i = 0; i < pieces.length; i++) {
      if (i > 0) {
        placements.push({ sku: couplerSku, role: 'coupler', model_glb: (pool[couplerSku] && pool[couplerSku].model_glb) || null, y_top_in: cursor, y_bottom_in: cursor - COUPLER_ADD });
        cursor = cursor - COUPLER_ADD;
      }
      var len = pieces[i].tele ? solved : pieces[i].nominal;
      if (len === null || len === undefined) { len = 0; }
      var pl = { sku: pieces[i].sku, role: pieces[i].tele ? 'pipe_tele' : 'pipe_fixed', model_glb: pieces[i].model_glb, y_top_in: cursor, y_bottom_in: cursor - len };
      if (pieces[i].tele) { pl.tele_solved_in = solved; pl.length_min_in = pieces[i].length_min; pl.length_max_in = pieces[i].length_max; }
      placements.push(pl);
      cursor = cursor - len;
    }
    var vesaY = cursor - headAdds;
    placements.push({ sku: headSku, role: 'head', model_glb: (pool[headSku] && pool[headSku].model_glb) || null, y_top_in: cursor, y_bottom_in: vesaY });

    out.placements = placements;
    out.vesa_y_in = vesaY;
    out.finished_in = -vesaY;

    // finished must match the card (spec 3 rule 6): finished_length_in for a
    // fixed card, else the committed length_in.
    var expected = (card.fixed && card.finished_length_in !== null && card.finished_length_in !== undefined)
      ? Number(card.finished_length_in) : target;
    if (expected !== null && expected !== undefined) { out.finished_delta_in = out.finished_in - expected; }

    // Availability: every placement needs a model. Collect the misses by SKU.
    var missing = [];
    var tris = 0;
    for (i = 0; i < placements.length; i++) {
      var plc = placements[i];
      if (!hasModel(plc.model_glb)) { missing.push(plc.sku); }
      if (plc.role === 'plate') { tris += TRI_CAP.ceiling_plate; }
      else if (plc.role === 'head') { tris += TRI_CAP.mounting_head; }
      else if (plc.role === 'coupler') { tris += TRI_CAP.coupler; }
      else if (plc.role === 'pipe_tele') { tris += TRI_CAP.pipe_tele; }
      else { tris += TRI_CAP.pipe_fixed; }
    }
    out.missing_models = missing;
    out.available = (missing.length === 0);
    out.tris_estimate = tris;
    out.tris_budget_ok = (tris <= SCENE_TRI_CAP);
    return out;
  }

  // =========================================================================
  // Everything below is the browser-only rendering side. It never runs under
  // the Node test harness (which loads this file and exercises computeChain
  // against fixtures), because it is all gated behind init()/show() and the
  // presence of a DOM and the three.js bundle.
  // =========================================================================

  var S = {
    // settings + pool, read once from the DOM
    wrap: null, container: null, toggle: null, statusEl: null, pool: null, settings: null,
    // three.js handles (filled after the bundle imports)
    THREE: null, GLTFLoader: null, OrbitControls: null, loader: null,
    renderer: null, scene: null, camera: null, controls: null, canvas: null,
    root: null, tvMesh: null, tvFrontMat: null, presetsEl: null, calloutsEl: null,
    dimEl: null, dimLineEl: null, dimLabelEl: null,
    // [STUDIO ENV 2026-09-22] the prefiltered studio environment (one per renderer)
    // and the measured PMREM build cost in ms.
    envTex: null, envMs: null,
    // [ROUND 4] content extents (inches) that drive the growing canvas height, the
    // applied canvas pixel height, and the current view azimuth/elevation for refit.
    contentH_in: null, appliedCanvasH: null, curAz: 35, curEl: 15,
    // caches and bookkeeping
    glbCache: {}, glbPromises: {}, groups: [], ro: null, animId: 0,
    orbiting: false, chainPoints: null,
    // [ROUND 2] gen is the show/hide generation token that defeats the double-
    // toggle race: show() and hide() both bump it, and every async continuation
    // re-checks its captured gen (and S.visible) before touching the renderer, so
    // a hide() landing mid-import() cannot leave a dead loop behind a built flag.
    // awaitingFrame keeps the loading text up until this pose's first frame.
    gen: 0, awaitingFrame: false, pendingOpenScroll: false, calloutsMeasured: false,
    // public-ish state
    visible: false, importing: false, bundleReady: false, built: false, disposed: false,
    firstFrameAt: null, showClickAt: null, tris: 0, lastChain: null, available: false,
    loadedUrls: [], assertions: {}, reposeTimer: 0,
    // [ROUND 5 REPOSE-RACE FIX 2026-09-20] monotonic re-pose sequence (owner item D, stacked chains).
    reposeSeq: 0,
    // [PDP 3D VARIANT 2026-09-20] optional injection set by init(container, opts); null / false keep today's behaviour.
    optGetBuild: null, optGetPool: null, hideTvCallout: false,
    // [PDP LENGTH 2026-09-22, director round 2] optional host hook: how many more
    // inches the drawn chain can still grow to without a re-fit, so the camera is
    // framed for the longest pose from the first fit. null = today's fit.
    optGetFitExtraIn: null,
    // [UX R1 2026-09-21] the rotate hint (proposals item 1). rotateHint is the
    // additive state() key: 'idle' before the first finished frame, then one of
    // 'shown', 'dismissed', 'suppressed' and final for this page load.
    rotateHint: 'idle', rotateHintSuppressed: false, hintEl: null, hintTimer: 0,
    // [UX R2 2026-09-21] the per part length labels (proposals item 4). partsEl is
    // the one container over the canvas; partLabels is one entry per drawn label
    // ({mid_in, el, w, h, px}); presetsBox is the preset buttons' rectangle,
    // measured ONCE PER POSE so the per frame pass stays pure arithmetic.
    // [PDP PART LABELS 2026-09-22, owner] partLabelMode is the additive init opt:
    // null = today's wording, 'sku' = the part number alone. topLeftBox is the
    // rectangle of an in-canvas control pinned top LEFT by the host (the PDP
    // modal's length pair), measured beside presetsBox and cleared the same way.
    partsEl: null, partLabels: [], partLabelsSuppressed: false, partLabelMode: null,
    presetsBox: null, presetsBoxW: -1, topLeftBox: null
  };

  // [ROUND 5 REPOSE-RACE FIX 2026-09-20] PURE guard for the stacked-repose race: a resolved load may
  // place ONLY if its sequence is still the newest; a later repose supersedes an earlier one.
  function reposeSuperseded(mySeq, currentSeq) { return mySeq !== currentSeq; }

  // [PDP LENGTH 2026-09-22] PURE. True when every GLB url this pose needs is
  // ALREADY in the cache, so the pose costs no network and can be drawn without
  // the loading status (the "quiet" re-pose the PDP length stepper asks for).
  // An empty list is trivially cached; a missing cache object is not.
  // [PDP LENGTH 2026-09-22, director round 2] PURE. The extra inches of HEADROOM
  // (really legroom: the chain hangs down from y = 0) a host wants the camera fit
  // to allow for, read from the injected getFitExtraIn. A host that can lengthen
  // the drawn chain without a re-fit (the PDP length stepper) returns how much
  // longer the chain can still get, so the FIRST fit already frames the longest
  // pose and the maximum is never drawn to get there. Anything that is not a
  // finite number >= 0, and any throw, is 0, which is exactly today's fit.
  function fitExtraIn(fn) {
    if (typeof fn !== 'function') { return 0; }
    var v;
    try { v = fn(); } catch (e) { return 0; }
    v = Number(v);
    if (!isFinite(v) || v <= 0) { return 0; }
    return v;
  }

  function allCached(urls, cache) {
    if (!urls || !urls.length) { return true; }
    if (!cache) { return false; }
    for (var i = 0; i < urls.length; i++) {
      if (!urls[i]) { continue; }
      if (!cache[urls[i]]) { return false; }
    }
    return true;
  }

  // ---- [UX R1 2026-09-21] the rotate hint (proposals item 1) ---------------
  // Nothing on the canvas told a first time visitor the model turns. The hint is
  // a small pill low on the canvas: an inline SVG rotate arrow plus the owner's
  // words. It is pointer-events:none so it can never eat the very drag it is
  // asking for, and aria-hidden because it is a visual affordance; the words
  // themselves go into the canvas's accessible name instead (see updateAria).
  var ROTATE_HINT_KEY = 'vmscfg3dRotateHintSeen';
  var ROTATE_HINT_MS = 6000;

  // PURE. The next hint state, given the current one. 'idle' is the ONLY state
  // that can still decide; every other value is final for this page load, and
  // that single fact is what stops a re-pose, a build change, a Front or Side
  // preset, or a close and reopen from bringing the hint back.
  function rotateHintNext(current, suppressed, seen) {
    if (current !== 'idle') { return current; }
    if (suppressed || seen) { return 'suppressed'; }
    return 'shown';
  }

  // Both take the storage object rather than reaching for localStorage, so a
  // throwing store is a test case and not a hypothesis. Both swallow: a browser
  // that refuses storage falls back to once per PAGE LOAD, which the 'idle' gate
  // above already gives, rather than losing the hint or showing it twice.
  function rotateHintSeen(store) {
    try { return !!(store && store.getItem(ROTATE_HINT_KEY)); } catch (e) { return false; }
  }
  function rememberRotateHint(store) {
    try { if (!store) { return false; } store.setItem(ROTATE_HINT_KEY, '1'); return true; } catch (e) { return false; }
  }
  function hintStore() {
    // Reading window.localStorage THROWS outright in some privacy modes, which is
    // why even the lookup is wrapped.
    try { return window.localStorage; } catch (e) { return null; }
  }

  // ---- [UX R2 2026-09-21] per part length labels (proposals item 4) --------
  // The 3D view drew ONE number, the finished length down the left edge. Every
  // per part number lived in the callouts strip under the canvas, so reading
  // "which of these pipes is the 36" meant looking away from the drawing. These
  // two functions are the whole geometry of the answer; everything else is DOM.
  // The gap pass is unchanged by the 2026-09-22 amendment: a coupler is only
  // 1.5 in of chain, so at most zooms its label is pushed below the joint it
  // names by the minimum gap. That is accepted; the alternative is a label that
  // sits on top of the pipe label above it.
  var PART_LABEL_GAP_PX = 18;   // minimum centre to centre gap after the layout pass
  var PART_LABEL_DX_PX = 28;    // to the RIGHT of the projected pipe axis
  var PART_LABEL_PAD_PX = 8;    // keep-inside-the-canvas margin, and the presets clearance

  // PURE. One entry per placement in the chain, top to bottom: the plate, every
  // pipe, EVERY COUPLER and the head.
  // [AMENDED 2026-09-22, owner] THE LABELS MUST ADD UP. He read the column as
  // arithmetic against the finished length on the left and found it 1.5 in short
  // per joint, because the couplers carried no label. computeChain builds
  // finished_in out of the plate, the pipes, 1.5 in per coupler and the head
  // (finished_in = -vesaY), so every placement has to print or the sum lies.
  // The words follow what the part does to the drop: the plate, the coupler and
  // the head ADD their length ("Plate adds 3.1 in"); a fixed pipe is its SKU and
  // its length ("NPT36 36.0 in"); a telescoping pipe is its SKU, the adjusted
  // word and its SOLVED length ("EXT2036 adjusted to 21.0 in"), which is what
  // y_top - y_bottom already is, with no special case.
  // A head whose adds_length_in is NEGATIVE prints its minus sign as it is. The
  // column is honest arithmetic before it is a tidy list.
  //
  // [PDP PART LABELS 2026-09-22, owner] THIRD ARGUMENT, PURE AND ADDITIVE.
  // "We should label the parts on the product page variants also. But only with
  // their part numbers not their dimensions." mode 'sku' prints p.sku and nothing
  // else, for every placement that carries one (a placement with no sku, which is
  // only ever a coupler on a stack whose card names none, is skipped rather than
  // printed blank). Any other mode value, and mode absent, is byte-identical to
  // today's wording, which is what keeps the configurator page and its suites
  // untouched. The arithmetic note above applies to the TEXT mode only: on the
  // product page there is no finished-length column to add up against, the labels
  // answer "which piece is which part number".
  function partLabelEntries(chain, settings, mode) {
    var out = [];
    if (!chain || !chain.placements) { return out; }
    var s = settings || {};
    var skuOnly = (mode === 'sku');
    for (var i = 0; i < chain.placements.length; i++) {
      var p = chain.placements[i], text = null;
      if (skuOnly) {
        if (!p.sku) { continue; }
        out.push({ role: p.role, text: p.sku, mid_in: (p.y_top_in + p.y_bottom_in) / 2 });
        continue;
      }
      var len = fmtIn(p.y_top_in - p.y_bottom_in);
      var sku = p.sku ? (p.sku + ' ') : '';
      if (p.role === 'plate') { text = (s.partPlate || 'Plate adds') + ' ' + len; }
      else if (p.role === 'coupler') { text = (s.partCoupler || 'Coupler adds') + ' ' + len; }
      else if (p.role === 'head') { text = (s.partHead || 'Head adds') + ' ' + len; }
      else if (p.role === 'pipe_tele') { text = sku + (s.partPipe || 'adjusted to') + ' ' + len; }
      else if (p.role === 'pipe_fixed') { text = sku + len; }
      if (text === null) { continue; }   // anything the chain grows later
      out.push({
        role: p.role,
        text: text,
        mid_in: (p.y_top_in + p.y_bottom_in) / 2
      });
    }
    return out;
  }

  // PURE. items is [{y}] in canvas pixels, top to bottom; the return is the y
  // each label should take. ONE top-down pass: a label closer to the one above
  // than minGapPx is pushed DOWN to exactly that gap, and nothing is ever moved
  // UP. That is what keeps the order of the labels the order of the parts: a
  // pass that could move a label up could swap two of them at a steep camera
  // angle and point the head number at a pipe.
  function layoutPartLabels(items, minGapPx) {
    var out = [];
    if (!items) { return out; }
    var gap = (typeof minGapPx === 'number' && minGapPx > 0) ? minGapPx : 0;
    var prev = null, i, y;
    for (i = 0; i < items.length; i++) {
      y = (items[i] && typeof items[i].y === 'number') ? items[i].y : 0;
      if (prev !== null && y < prev + gap) { y = prev + gap; }
      out.push(y);
      prev = y;
    }
    return out;
  }

  function readSettings(wrap) {
    function d(name, fallback) { var v = wrap.getAttribute(name); return (v === null || v === undefined) ? fallback : v; }
    return {
      bundleUrl: d('data-bundle-url', ''),
      loading: d('data-label-loading', 'Loading the 3D view'),
      unavailable: d('data-label-unavailable', 'The 3D view is not available for this combination yet.'),
      front: d('data-label-front', 'Front'),
      side: d('data-label-side', 'Side'),
      finished: d('data-label-finished', 'Finished length'),
      mast: d('data-label-mast', 'Mast'),
      tv: d('data-label-tv', 'TV'),
      // [ROUND 3 2026-09-16] TV fill opacity, a 0 to 100 percent section setting.
      // 0 = only the outline draws (the owner's "transparent" case); 100 = the
      // old opaque box. Read here alongside the data-label-* attributes.
      tvOpacity: parseTvOpacity(wrap.getAttribute('data-tv-opacity')),
      toggleOff: d('data-label-toggle-off', '3D view'),
      toggleOn: d('data-label-toggle-on', 'Hide 3D view'),
      // [UX R1 2026-09-21] the rotate hint's words, read next to the neighbours.
      rotate: d('data-label-rotate', 'Drag to rotate'),
      // [UX R2 2026-09-21, amended 2026-09-22] the four per part words, read the
      // same way. partPipe is the TELESCOPING word only: a fixed pipe prints its
      // SKU and its length with no extra word.
      partPlate: d('data-label-part-plate', 'Plate adds'),
      partPipe: d('data-label-part-pipe', 'adjusted to'),
      partHead: d('data-label-part-head', 'Head adds'),
      partCoupler: d('data-label-part-coupler', 'Coupler adds')
    };
  }

  function parsePool() {
    // The pool is the same JSON the rest of the configurator reads.
    var map = {};
    try {
      var el = document.getElementById('vmscfg-data');
      if (!el) { return map; }
      var json = JSON.parse(el.textContent || '{}');
      var comps = json.components || [];
      for (var i = 0; i < comps.length; i++) {
        var c = comps[i];
        if (c && c.sku) { map[c.sku] = c; }
      }
    } catch (e) { /* a missing or malformed pool just yields "not available" */ }
    return map;
  }

  function getBuild() {
    try {
      // [PDP 3D VARIANT 2026-09-20] an injected build source wins; else today's global.
      if (S.optGetBuild) { return S.optGetBuild(); }
      if (window.VMSConfiguratorPage && typeof window.VMSConfiguratorPage.getBuild === 'function') {
        return window.VMSConfiguratorPage.getBuild();
      }
    } catch (e) { /* ignore */ }
    return null;
  }

  function setStatus(text) {
    if (!S.statusEl) { S.statusEl = S.container.querySelector('[data-vmscfg-3d-status]'); }
    if (S.statusEl) {
      S.statusEl.textContent = text; S.statusEl.hidden = false;
      // [LOADING RING 2026-09-20] Adopt the CSS-only loader ring (assets/vms-configurator-3d.css
      // .vmscfg-3d__status.is-loading::before, added with the slanted view) - owner ruling. The ring
      // shows ONLY while the status carries is-loading, i.e. only for the loading text; the
      // not-available / error texts clear it, and hideStatus (first rendered frame) clears it too.
      if (S.settings && text === S.settings.loading) { S.statusEl.classList.add('is-loading'); }
      else { S.statusEl.classList.remove('is-loading'); }
    }
  }
  function hideStatus() { if (S.statusEl) { S.statusEl.hidden = true; S.statusEl.classList.remove('is-loading'); } }

  // ---- init: idempotent, cheap, NO three.js -------------------------------
  function init(container, opts) {
    if (S.wrap) { return; }
    opts = opts || {};
    var wrap = (container && container.closest) ? container.closest('.vmscfg-3d-wrap') : document.querySelector('.vmscfg-3d-wrap');
    if (!wrap) { return; }
    S.wrap = wrap;
    // [PDP 3D VARIANT 2026-09-20] ADDITIVE, default-preserving injection. When opts
    // carries getBuild / getPool the engine is driven from another page (the PDP
    // variant modal, assets/vms-pdp-3d.js). With no opts every lookup below falls back
    // to EXACTLY today's globals and DOM ids, so the configurator page and its suites
    // are unchanged. hideTvCallout drops the TV callout row where no TV is drawn.
    S.optGetBuild = (typeof opts.getBuild === 'function') ? opts.getBuild : null;
    S.optGetPool = (typeof opts.getPool === 'function') ? opts.getPool : null;
    S.hideTvCallout = !!opts.hideTvCallout;
    // [PDP LENGTH 2026-09-22, director round 2] stored exactly like the three
    // above: absent (or not a function) leaves the fit at today's contentBox.
    S.optGetFitExtraIn = (typeof opts.getFitExtraIn === 'function') ? opts.getFitExtraIn : null;
    // [UX R1 2026-09-21] SCOPE GATE. The rotate hint is the configurator page's
    // affordance this round; an injected init (the PDP variant modal) keeps
    // exactly today's behaviour, so the hint never shows and the canvas's
    // accessible name is left alone there.
    S.rotateHintSuppressed = !!(S.optGetBuild || S.optGetPool || S.hideTvCallout);
    if (S.rotateHintSuppressed) { S.rotateHint = 'suppressed'; }
    // [UX R2 2026-09-21] SAME SCOPE GATE for the per part labels (proposals item
    // 4). The PDP variant modal was approved as it stands, so an injected init
    // builds no labels at all and state().partLabels reads 'suppressed'.
    // [PDP PART LABELS 2026-09-22, owner] ...UNLESS the host asks for them by the
    // additive opt partLabelMode: 'sku' turns the labels ON and prints the part
    // number alone. Absent (or any other value) leaves the gate exactly as it was:
    // configurator page = full text, any injected init = suppressed.
    S.partLabelMode = (opts.partLabelMode === 'sku') ? 'sku' : null;
    S.partLabelsSuppressed = S.rotateHintSuppressed && !S.partLabelMode;
    S.container = wrap.querySelector('#elev3d') || wrap.querySelector('.vmscfg-3d');
    S.toggle = wrap.querySelector('#elev3dToggle') || wrap.querySelector('.vmscfg-3d__toggle');
    S.statusEl = wrap.querySelector('[data-vmscfg-3d-status]');
    S.settings = readSettings(wrap);
    S.pool = S.optGetPool ? S.optGetPool() : parsePool();
    if (S.toggle) {
      S.toggle.addEventListener('click', function () {
        if (S.visible) { hide(); } else { show(); }
      });
    }
    // Re-pose on every solved-build change, debounced (spec 4.9). Only in default
    // (configurator) mode; the PDP modal drives its own re-pose by reopening.
    if (!S.optGetBuild) {
      document.addEventListener('vmscfg:build-changed', function () {
        if (!S.visible) { return; }
        if (S.reposeTimer) { clearTimeout(S.reposeTimer); }
        S.reposeTimer = setTimeout(function () { repose(); }, 50);
      });
    }
  }

  // ---- the bundle: dynamic import() on first show only --------------------
  // [WARN, ROUND 2] Shopify's CDN minifies theme .js and DOWNLEVELS a syntactic
  // import() to require() (README "SHOPIFY'S CDN MINIFIES THEME .js ASSETS"), so
  // a bare import(url) ships as require(url) and throws "require is not defined".
  // We keep a REAL dynamic import() but hide it from that minifier inside a
  // Function body (a string it will not rewrite); if a storefront CSP ever blocks
  // Function (unsafe-eval), we fall back to a module <script> that imports the
  // bundle and hands its namespace back. Verified on the live DEV preview.
  function importBundle(url) {
    try {
      var dyn = new Function('u', 'return import(u);');
      return dyn(url);
    } catch (e) {
      return importViaModuleTag(url);
    }
  }
  function importViaModuleTag(url) {
    return new Promise(function (resolve, reject) {
      var cb = '__vms3d_' + Date.now();
      window[cb] = function (m) { try { delete window[cb]; } catch (x) {} resolve(m); };
      var s = document.createElement('script');
      s.type = 'module';
      s.textContent = 'import * as m from ' + JSON.stringify(url) + '; window[' + JSON.stringify(cb) + '](m);';
      s.onerror = function () { reject(new Error('module script failed: ' + url)); };
      document.head.appendChild(s);
      setTimeout(function () { if (window[cb]) { reject(new Error('bundle import timed out: ' + url)); } }, 20000);
    });
  }

  function ensureBundle() {
    if (S.bundleReady) { return Promise.resolve(true); }
    if (S.importing) { return S.importing; }
    if (!S.settings || !S.settings.bundleUrl) { return Promise.reject(new Error('no bundle url')); }
    var pr = importBundle(S.settings.bundleUrl).then(function (mod) {
      S.THREE = mod.THREE; S.GLTFLoader = mod.GLTFLoader; S.OrbitControls = mod.OrbitControls;
      S.loader = new S.GLTFLoader();
      S.bundleReady = true;
      return true;
    });
    // Clear the cached promise on failure so a later toggle can retry the load.
    pr.catch(function () { if (S.importing === pr) { S.importing = null; } });
    S.importing = pr;
    return pr;
  }

  // ---- show / hide --------------------------------------------------------
  function show() {
    if (!S.wrap) { return Promise.resolve(false); }
    // [ROUND 2] Take a generation token; the async continuations below abandon
    // quietly if a hide()/show() supersedes this one mid-load.
    S.gen++;
    var myGen = S.gen;
    S.showClickAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
    S.firstFrameAt = null; // reset so each open measures its OWN first frame (cold vs cached)
    S.visible = true; S.disposed = false;
    if (S.container) { S.container.hidden = false; }
    // [ROUND 4] Mobile only: on OPEN (this is the toggle-click entry; re-poses do
    // not call show()), put the canvas container top at the viewport top so the 3D
    // view fills the phone screen from the top down. Deferred to the FIRST rendered
    // frame (see the loop) so the canvas already has its content height and the page
    // is tall enough to reach the top. Never on desktop, never on a re-pose.
    S.pendingOpenScroll = true;
    if (S.toggle) { S.toggle.setAttribute('aria-pressed', 'true'); S.toggle.textContent = S.settings.toggleOn; }
    setStatus(S.settings.loading);
    return ensureBundle().then(function () {
      if (S.gen !== myGen || !S.visible) { return false; } // superseded by a hide()/show() mid-import
      buildRenderer();
      repose(myGen);
      return true;
    }).catch(function (e) {
      if (S.gen !== myGen) { return false; }
      setStatus(S.settings.unavailable);
      if (S.container) { S.container.setAttribute('data-vmscfg-3d-error', String(e && e.message ? e.message : e)); }
      // [ROUND 4c] Bundle-load failure is still an open outcome: align the status to
      // the viewport top on mobile too, then clear the flag.
      if (S.pendingOpenScroll && S.visible) { S.pendingOpenScroll = false; scrollCanvasToTopIfMobile(); }
      return false;
    });
  }

  function hide() {
    S.gen++; // [ROUND 2] invalidate any in-flight show() continuation
    S.visible = false;
    if (S.container) { S.container.hidden = true; }
    if (S.toggle) { S.toggle.setAttribute('aria-pressed', 'false'); S.toggle.textContent = S.settings.toggleOff; }
    disposeGL();
  }

  // ---- renderer / scene ---------------------------------------------------
  // [ROUND 4b] The canvas height tracks the build's VERTICAL extent against a FIXED
  // reference width, so a longer mount is ALWAYS a taller canvas regardless of the
  // TV width (the earlier ratio against the content's own width made a wide TV
  // shorter than a narrow one at the same length). REF_W_IN = 48 is the cabinet
  // width of a 55 in 16:9 TV, the reference build. The height is then capped so the
  // callout strip below stays fully visible: hMax = viewportH - calloutsH - 16 (a
  // 16 px breathing margin); absolute cap 1100; floor 340.
  var REF_W_IN = 48;
  function canvasHeight(w, contentH_in, viewportH, calloutsH) {
    var aspect = 0.72;
    if (contentH_in && contentH_in > 0) {
      aspect = Math.max(0.72, contentH_in / REF_W_IN);
    }
    var h = Math.round(w * aspect);
    var floor = 340;
    var ceil = 1100;
    if (typeof viewportH === 'number' && viewportH > 0) {
      var ch = (typeof calloutsH === 'number' && calloutsH >= 0) ? calloutsH : 0;
      var hMax = Math.round(viewportH - ch - 16);
      if (hMax < ceil) { ceil = hMax; } // the viewport cap can only lower the 1100 cap
    }
    if (ceil < floor) { ceil = floor; } // never invert the clamp on a tiny viewport
    if (h < floor) { h = floor; }
    if (h > ceil) { h = ceil; }
    return h;
  }

  function calloutsHeightPx() {
    // [ROUND 4c] Use the measured strip height ONLY once it has actually been
    // rendered for this pose (S.calloutsMeasured), so a real 0 is not confused with
    // "not rendered yet"; before that use a phone/desktop estimate. The size is
    // re-applied after updateCallouts, when the real height is known.
    if (S.calloutsMeasured && S.calloutsEl) { return S.calloutsEl.offsetHeight; }
    var vw = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth : 320;
    return (vw <= 749) ? 56 : 28;
  }

  function containerSize() {
    var w = Math.max(240, Math.floor((S.container && S.container.clientWidth) || 320));
    var vh = (typeof window !== 'undefined' && window.innerHeight) ? window.innerHeight : 0;
    var h = canvasHeight(w, S.contentH_in, vh, calloutsHeightPx());
    return { w: w, h: h };
  }

  // Apply the content-driven canvas size to the renderer and camera. Safe to call
  // repeatedly (idempotent for a fixed width / content / viewport).
  function applyCanvasSize() {
    if (!S.renderer || !S.camera) { return; }
    var sz = containerSize();
    S.renderer.setSize(sz.w, sz.h, false);
    S.canvas.style.width = '100%';
    S.canvas.style.height = 'auto';
    S.camera.aspect = sz.w / sz.h;
    S.camera.updateProjectionMatrix();
    S.appliedCanvasH = sz.h;
  }

  // Mobile-only align-on-open: scroll the #elev3d container top to the viewport
  // top, below the stuck header (sticky header round 2026-09-28). Scrolling is a
  // nicety; a throw must never break the toggle.
  // [ROUND 4c] Deferred two frames so the just-changed layout (canvas shown or
  // hidden, callouts rendered) has settled before we read the container position
  // and scroll; the unavailable path scrolls a collapsed view, where a synchronous
  // scroll clamped short on a tall viewport because the reflow had not landed yet.
  function scrollCanvasToTopIfMobile() {
    var doScroll = function () {
      try {
        if (!S.visible || !S.container) { return; }
        var vw = (typeof window !== 'undefined' && window.innerWidth) ? window.innerWidth : 0;
        if (vw <= 0 || vw > 749) { return; } // phones only (theme breakpoint 749)
        var rect = S.container.getBoundingClientRect();
        var pageY = rect.top + (window.scrollY || window.pageYOffset || 0) - (parseFloat(getComputedStyle(document.querySelector('main') || document.body).scrollMarginTop) || 0); // sticky header round 2026-09-28: clear the stuck header
        window.scrollTo(0, Math.max(0, Math.round(pageY)));
      } catch (e) { /* ignore */ }
    };
    if (typeof requestAnimationFrame === 'function') { requestAnimationFrame(function () { requestAnimationFrame(doScroll); }); }
    else { doScroll(); }
  }

  function readBgColor() {
    // Match the 2D scene background (spec 4.7).
    try {
      var elev = document.getElementById('elevScene');
      if (elev) {
        var bg = getComputedStyle(elev).backgroundColor;
        if (bg && bg !== 'rgba(0, 0, 0, 0)' && bg !== 'transparent') { return bg; }
      }
    } catch (e) { /* ignore */ }
    return '#eef0f2';
  }

  // ---- [STUDIO ENV 2026-09-22] the procedural studio room ------------------
  // The mount GLBs are "Paint Matte Black" (baseColorFactor 0.0025, metalness 0,
  // roughness 0.46), so nearly everything the customer sees is specular
  // reflection, not diffuse colour. A HemisphereLight contributes NO specular in
  // three.js, which left exactly one hot spot from the key light and a part that
  // reads black. Shopify's model-viewer always carries a default image based
  // environment; this gives our two engines the same kind of fill.
  // three's examples/jsm RoomEnvironment is not in our bundle (core only), so the
  // room is built here: a grey shell plus emissive panels, prefiltered once per
  // renderer by PMREMGenerator and kept on S.envTex.
  var STUDIO_SHELL = 0x08080a;   // the box the panels sit inside
  var STUDIO_TOP = 9.0;          // broad overhead softbox
  var STUDIO_SIDE = 5.0;         // left and right, mid height
  var STUDIO_FRONT = 6.0;        // camera side: the face the customer looks at
  var STUDIO_BACK = 8.5;         // rim from behind
  var STUDIO_ENV_INTENSITY = 0.31;
  var STUDIO_EXPOSURE = 1.0;
  var STUDIO_HEMI = 0.12;        // was 1.05 before the environment carried the fill
  var STUDIO_KEY = 0.49;          // was 1.1; still the highlight source

  function buildStudioEnvironment(THREE, renderer) {
    var room = new THREE.Scene();
    var junk = [];
    // Emissive, not lit: a PMREM scene of ours carries no lights, and a purely
    // lit MeshStandardMaterial would prefilter to black.
    var shellGeo = new THREE.BoxGeometry(10, 10, 10);
    var shellMat = new THREE.MeshStandardMaterial({
      color: STUDIO_SHELL, emissive: STUDIO_SHELL, emissiveIntensity: 1,
      roughness: 1, metalness: 0, side: THREE.BackSide
    });
    room.add(new THREE.Mesh(shellGeo, shellMat));
    junk.push(shellGeo, shellMat);

    function panel(w, h, d, x, y, z, intensity) {
      var geo = new THREE.BoxGeometry(w, h, d);
      var mat = new THREE.MeshBasicMaterial();
      mat.color.setScalar(intensity); // above 1 is legal: the PMREM target is half float
      var mesh = new THREE.Mesh(geo, mat);
      mesh.position.set(x, y, z);
      room.add(mesh);
      junk.push(geo, mat);
    }
    panel(5.0, 0.2, 5.0, 0.0, 4.6, 0.0, STUDIO_TOP);
    panel(0.2, 3.0, 4.0, -4.6, 0.6, 0.0, STUDIO_SIDE);
    panel(0.2, 3.0, 4.0, 4.6, 0.6, 0.0, STUDIO_SIDE);
    panel(4.5, 3.0, 0.2, 0.0, 0.6, 4.6, STUDIO_FRONT);
    panel(4.5, 3.0, 0.2, 0.0, 0.6, -4.6, STUDIO_BACK);

    var pmrem = new THREE.PMREMGenerator(renderer);
    var tex = pmrem.fromScene(room, 0.04).texture;
    pmrem.dispose();
    while (room.children.length) { room.remove(room.children[0]); }
    for (var i = 0; i < junk.length; i++) { try { junk[i].dispose(); } catch (e) { /* ignore */ } }
    return tex;
  }

  // Applies the environment and ACES tone mapping to the scene just built. Called
  // once per renderer; a re-pose or a rebuild never makes a second PMREM.
  function applyStudioEnvironment(THREE) {
    S.envTex = null; S.envMs = null;
    try {
      var t0 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      S.envTex = buildStudioEnvironment(THREE, S.renderer);
      var t1 = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
      S.envMs = Math.round((t1 - t0) * 10) / 10;
      S.scene.environment = S.envTex;
      S.scene.environmentIntensity = STUDIO_ENV_INTENSITY;
      S.renderer.toneMapping = THREE.ACESFilmicToneMapping;
      S.renderer.toneMappingExposure = STUDIO_EXPOSURE;
    } catch (e) {
      // Never let a lighting nicety stop the view drawing.
      S.envTex = null; S.envMs = null;
      try { S.scene.environment = null; } catch (e2) { /* ignore */ }
    }
  }

  function buildRenderer() {
    // [ROUND 2] Guard on the renderer's existence, NOT S.built: dispose() nulls
    // S.renderer, so the next show() rebuilds cleanly and a stale flag can't block it.
    if (S.renderer) { return; }
    var THREE = S.THREE;
    var sz = containerSize();
    S.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, preserveDrawingBuffer: false });
    S.renderer.setPixelRatio(Math.min((window.devicePixelRatio || 1), 2)); // DPR capped at 2 (spec 4.8)
    S.renderer.setSize(sz.w, sz.h, false);
    S.canvas = S.renderer.domElement;
    S.canvas.setAttribute('role', 'img');
    S.canvas.setAttribute('aria-label', S.settings.loading);
    S.canvas.style.width = '100%';
    S.canvas.style.height = 'auto';
    S.canvas.style.maxWidth = '100%';

    S.scene = new THREE.Scene();
    try { S.scene.background = new THREE.Color(readBgColor()); } catch (e2) { S.scene.background = new THREE.Color('#eef0f2'); }

    S.camera = new THREE.PerspectiveCamera(35, sz.w / sz.h, 0.01, 100);

    // [STUDIO ENV 2026-09-22] The prefiltered studio room goes on FIRST: the two
    // lights below are now trim on top of it, not the whole lighting budget. The
    // background stays the flat page colour set above, never the environment.
    applyStudioEnvironment(THREE);

    // Lighting: one hemisphere plus one directional key (spec 4.7), both lowered
    // in 2026-09-22 now that scene.environment carries the fill.
    var hemi = new THREE.HemisphereLight(0xffffff, 0x707078, STUDIO_HEMI);
    S.scene.add(hemi);
    var key = new THREE.DirectionalLight(0xffffff, STUDIO_KEY);
    key.position.set(1.2, 2.0, 1.6);
    S.scene.add(key);

    S.root = new THREE.Group();
    S.scene.add(S.root);

    // The container currently holds only the status line; append the canvas,
    // the preset buttons, the dimension overlay and the callout strip.
    S.container.appendChild(S.canvas);
    buildPresets();
    buildDimOverlay();
    buildRotateHint();
    buildCallouts();

    S.controls = new S.OrbitControls(S.camera, S.canvas);
    S.controls.enableDamping = true;
    S.controls.dampingFactor = 0.08;
    // [UX R1 2026-09-21] The first drag IS the answer to the hint, so the same
    // 'start' event that hides the dimension overlay retires it.
    S.controls.addEventListener('start', function () { S.orbiting = true; if (S.dimEl) { S.dimEl.classList.add('is-hidden'); } dismissRotateHint(); });
    S.controls.addEventListener('end', function () { S.orbiting = false; });

    // Resize observer on the container (spec 4.8).
    if (window.ResizeObserver) {
      S.ro = new ResizeObserver(function () { onResize(); });
      S.ro.observe(S.container);
    } else {
      window.addEventListener('resize', onResize);
    }

    S.built = true;
    startLoop();
  }

  // [UX R3 2026-09-22] Item 11: a small inline SVG before each preset's words, a
  // screen seen face on for Front and the mast and head seen edge on for Side. The
  // icons are aria-hidden and not focusable, and the owner's words still go in with
  // textContent AFTER them, so each button's accessible name is exactly the setting
  // text ("Front", "Side"). ADDITIVE and init-option free: this engine also draws
  // the PDP 3D modal and the slanted wall view, and the icons appear there too on
  // purpose.
  var PRESET_ICON_OPEN = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">';
  var PRESET_ICON_FRONT = PRESET_ICON_OPEN + '<rect x="3" y="5" width="18" height="11" rx="1.5"/><path d="M12 2v3"/></svg>';
  var PRESET_ICON_SIDE = PRESET_ICON_OPEN + '<path d="M12 2v7"/><rect x="10" y="9" width="4" height="12" rx="1"/><path d="M6 15h4"/></svg>';

  function presetButton(iconSvg, label) {
    var b = document.createElement('button');
    b.type = 'button'; b.className = 'vmscfg-3d__preset';
    b.innerHTML = iconSvg;
    b.appendChild(document.createTextNode(label == null ? '' : String(label)));
    return b;
  }

  function buildPresets() {
    var el = document.createElement('div');
    el.className = 'vmscfg-3d__presets';
    var front = presetButton(PRESET_ICON_FRONT, S.settings.front);
    front.addEventListener('click', function () { setView(0, 8); });
    var side = presetButton(PRESET_ICON_SIDE, S.settings.side);
    side.addEventListener('click', function () { setView(90, 8); });
    el.appendChild(front); el.appendChild(side);
    S.container.appendChild(el);
    S.presetsEl = el;
  }

  // [UX R1 2026-09-21] The hint element, built once with the renderer and left in
  // the DOM at opacity 0 so the fade has something to fade. The icon is an inline
  // SVG (no glyph, no image, no extra request) and is the ONLY thing written as
  // HTML here; the owner's words go in with textContent.
  var ROTATE_ICON_SVG = '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" ' +
    'stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false">' +
    '<path d="M20.5 12a8.5 8.5 0 1 1-2.49-6.01"/><polyline points="20.5 3.5 20.5 9 15 9"/></svg>';

  function buildRotateHint() {
    var el = document.createElement('div');
    el.className = 'vmscfg-3d__hint';
    el.setAttribute('aria-hidden', 'true');
    el.innerHTML = ROTATE_ICON_SVG;
    var t = document.createElement('span');
    t.className = 'vmscfg-3d__hint-text';
    t.textContent = S.settings.rotate;
    el.appendChild(t);
    S.container.appendChild(el);
    S.hintEl = el;
  }

  // Called from the frame loop the moment the FIRST finished frame of this view
  // has drawn, which is the only place that knows the loading ring is down and
  // real parts are on screen. Never called on the unavailable sentence: that
  // path never sets awaitingFrame false through the loop.
  function maybeShowRotateHint() {
    var next = rotateHintNext(S.rotateHint, S.rotateHintSuppressed, rotateHintSeen(hintStore()));
    if (next === S.rotateHint) { return; }
    S.rotateHint = next;
    if (next !== 'shown' || !S.hintEl) { return; }
    S.hintEl.classList.add('is-shown');
    if (S.hintTimer) { clearTimeout(S.hintTimer); }
    S.hintTimer = setTimeout(function () { dismissRotateHint(); }, ROTATE_HINT_MS);
  }

  // The first drag, the 6 second timer, or the view being torn down. Writing the
  // storage key here rather than at show time means a hint that was never
  // actually painted (storage refused, element missing) cannot burn the one
  // showing this browser gets.
  function dismissRotateHint() {
    if (S.hintTimer) { clearTimeout(S.hintTimer); S.hintTimer = 0; }
    if (S.rotateHint !== 'shown') { return; }
    S.rotateHint = 'dismissed';
    if (S.hintEl) { S.hintEl.classList.remove('is-shown'); }
    rememberRotateHint(hintStore());
  }

  function buildDimOverlay() {
    var el = document.createElement('div');
    el.className = 'vmscfg-3d__dim is-hidden';
    var line = document.createElement('span'); line.className = 'vmscfg-3d__dim-line';
    var label = document.createElement('span'); label.className = 'vmscfg-3d__dim-label';
    el.appendChild(line); el.appendChild(label);
    S.container.appendChild(el);
    S.dimEl = el; S.dimLineEl = line; S.dimLabelEl = label;
  }

  // [UX R2 2026-09-21] The per part label container (proposals item 4). One div
  // over the canvas holding one span per part, rebuilt whenever the chain is
  // re-posed because the parts themselves change. aria-hidden and
  // pointer-events:none: it is a reading aid drawn on top of the picture, and the
  // callouts strip below the canvas is still the accessible copy of the numbers.
  function buildPartLabels(chain) {
    if (S.partLabelsSuppressed || !S.container) { S.partLabels = []; return; }
    if (!S.partsEl) {
      var box = document.createElement('div');
      box.className = 'vmscfg-3d__parts';
      box.setAttribute('aria-hidden', 'true');
      S.container.appendChild(box);
      S.partsEl = box;
    }
    while (S.partsEl.firstChild) { S.partsEl.removeChild(S.partsEl.firstChild); }
    var entries = partLabelEntries(chain, S.settings, S.partLabelMode);
    S.partLabels = [];
    for (var i = 0; i < entries.length; i++) {
      var sp = document.createElement('span');
      sp.className = 'vmscfg-3d__part';
      sp.textContent = entries[i].text;
      S.partsEl.appendChild(sp);
      S.partLabels.push({ mid_in: entries[i].mid_in, el: sp, w: 0, h: 0, px: null });
    }
    // ONCE PER POSE, not per frame: the preset buttons sit top right and the plate
    // label sits at the very top of the chain, so at 375px the two want the same
    // corner. Measuring the buttons here keeps the frame pass pure arithmetic;
    // updatePartLabels measures again only when the canvas width changes (a
    // resize moves the top right corner without a re-pose).
    measurePresetsBox(-1);
  }

  function measurePresetsBox(width) {
    S.presetsBox = S.presetsEl ? {
      left: S.presetsEl.offsetLeft,
      bottom: S.presetsEl.offsetTop + S.presetsEl.offsetHeight
    } : null;
    // [PDP PART LABELS 2026-09-22] The host's own in-canvas control, top LEFT.
    // The engine does not know the PDP modal's class names, so the contract is a
    // marker attribute on any absolutely positioned child of the container:
    // data-vmscfg-3d-topleft. Offsets, not getBoundingClientRect, for the same
    // reason the presets are measured that way: the labels are placed in CONTAINER
    // space. A hidden control measures 0 wide and counts as absent.
    var tl = S.container ? S.container.querySelector('[data-vmscfg-3d-topleft]') : null;
    S.topLeftBox = (tl && tl.offsetWidth > 0) ? {
      right: tl.offsetLeft + tl.offsetWidth,
      bottom: tl.offsetTop + tl.offsetHeight
    } : null;
    S.presetsBoxW = width;
  }

  // Called from the frame loop right after updateDimOverlay, for the same reason
  // that one is: the labels are pinned to projected points, so they move with
  // every camera change and there is no event that covers all of them.
  function updatePartLabels() {
    if (!S.partsEl || !S.partLabels.length || !S.available || !S.canvas || !S.THREE) { return; }
    // Hidden while orbiting and back on the next frame after release, exactly as
    // the dimension overlay behaves: a number pinned to a point that is sliding
    // across the canvas reads as a bug.
    if (S.orbiting) { S.partsEl.classList.add('is-hidden'); return; }
    S.partsEl.classList.remove('is-hidden');
    var rect = S.canvas.getBoundingClientRect();
    if (S.presetsBoxW !== rect.width) { measurePresetsBox(rect.width); }
    var pts = [], i, L;
    for (i = 0; i < S.partLabels.length; i++) {
      L = S.partLabels[i];
      // First honest measurement: the span has to be in the DOM and painted for
      // offsetWidth to mean anything, which it is not at build time on a cold open.
      if (!L.w) { L.w = L.el.offsetWidth; L.h = L.el.offsetHeight; }
      L.px = project(new S.THREE.Vector3(0, L.mid_in * IN_TO_M, 0));
      pts.push({ y: L.px.y });
    }
    // The presets clearance, applied BEFORE the gap pass so one pushed label
    // carries the ones below it down with it rather than piling up under it.
    if (S.presetsBox) {
      for (i = 0; i < S.partLabels.length; i++) {
        L = S.partLabels[i];
        if (L.px.x + PART_LABEL_DX_PX + L.w > S.presetsBox.left &&
            pts[i].y - L.h / 2 < S.presetsBox.bottom + PART_LABEL_PAD_PX) {
          pts[i].y = S.presetsBox.bottom + PART_LABEL_PAD_PX + L.h / 2;
        }
      }
    }
    // [PDP PART LABELS 2026-09-22] The SAME clearance against a control pinned top
    // LEFT (the PDP modal's "- 1 in / value / + 1 in" pair). A label is pushed only
    // when it would actually land on the control: its left edge starts left of the
    // control's right edge plus the pad AND its box overlaps the control's rows.
    // Also applied BEFORE the gap pass, so the first label carries the rest down.
    if (S.topLeftBox) {
      for (i = 0; i < S.partLabels.length; i++) {
        L = S.partLabels[i];
        if (L.px.x + PART_LABEL_DX_PX < S.topLeftBox.right + PART_LABEL_PAD_PX &&
            pts[i].y - L.h / 2 < S.topLeftBox.bottom + PART_LABEL_PAD_PX) {
          pts[i].y = S.topLeftBox.bottom + PART_LABEL_PAD_PX + L.h / 2;
        }
      }
    }
    var ys = layoutPartLabels(pts, PART_LABEL_GAP_PX);
    for (i = 0; i < S.partLabels.length; i++) {
      L = S.partLabels[i];
      // A part whose midpoint projects off the canvas has nothing to point at.
      if (L.px.y < 0 || L.px.y > rect.height) { L.el.style.display = 'none'; continue; }
      L.el.style.display = '';
      var x = L.px.x + PART_LABEL_DX_PX;
      var maxX = rect.width - PART_LABEL_PAD_PX - L.w;   // the right edge stays inside
      if (x > maxX) { x = Math.max(0, maxX); }
      L.el.style.left = x + 'px';
      L.el.style.top = (ys[i] - L.h / 2) + 'px';
    }
  }

  function buildCallouts() {
    var el = document.createElement('div');
    el.className = 'vmscfg-3d__callouts';
    S.calloutsEl = el;
    // The callout strip sits under the canvas but inside the wrap so it stays
    // with the 3D view; append to the wrap after the container.
    S.wrap.appendChild(el);
  }

  // ---- the chain -> the scene --------------------------------------------
  function showUnavailable(build, chain) {
    setStatus(S.settings.unavailable);
    if (S.canvas) { S.canvas.style.display = 'none'; }
    if (S.presetsEl) { S.presetsEl.style.display = 'none'; }
    if (S.dimEl) { S.dimEl.classList.add('is-hidden'); }
    // [UX R1 2026-09-21] The hint never rides the unavailable sentence: there is no
    // model to turn. It cannot be SHOWN on this path (only a finished frame shows
    // it), so this only covers a build change that makes a live view uncoverable
    // while the hint is still up.
    dismissRotateHint();
    updateCallouts(build, chain, false);
    // [ROUND 4c] The mobile align-on-open must fire on EVERY open outcome, not just
    // the covered-build first frame: consume the pending open-scroll here too, so an
    // uncovered build (and the GLB-failure catch, which routes here) still aligns the
    // status to the viewport top. Callers already applied the gen guard; S.visible is
    // re-checked, and scrollCanvasToTopIfMobile itself gates on the phone breakpoint.
    if (S.pendingOpenScroll && S.visible) { S.pendingOpenScroll = false; scrollCanvasToTopIfMobile(); }
  }

  // [PDP LENGTH 2026-09-22] ADDITIVE opts, the same contract as init(): with opts
  // absent every path below is exactly today's.
  //   keepCamera: skip the fitCamera() after placement, so a re-pose that only
  //     changes the telescoping length does not re-frame the shot (the PDP length
  //     stepper fits ONCE at the range maximum and then steps inside that frame).
  //     S.contentH_in is carried across, because clearGroups nulls it and the
  //     canvas size is only recomputed inside fitCamera.
  //   quiet: skip the loading status when every GLB of the new chain is already
  //     cached (allCached), so stepping the length never flashes a spinner. A url
  //     that is NOT cached still shows the loading status exactly as today.
  function repose(gen, opts) {
    if (gen === undefined) { gen = S.gen; }
    opts = opts || {};
    // [ROUND 2] renderer-existence gate, plus the gen/visible re-check.
    if (!S.renderer || !S.visible || S.gen !== gen) { return; }
    // [ROUND 5 REPOSE-RACE FIX 2026-09-20] OWNER ITEM D (stacked chains during a cold load). Every
    // vmscfg:build-changed can start a repose with the SAME S.gen (gen only changes on show/hide); the
    // 50 ms debounce only coalesces changes WITHIN its window, and loadGLB shares one in-flight promise
    // per url, so pending reposes could resolve together and each run placeChain, stacking N chains that
    // clearGroups never removed. FIX: take a monotonic sequence here; after the load only the NEWEST
    // sequence may place (stale ones return without touching the status/ring), placeChain clears the
    // groups itself immediately before placing, and the build is RE-READ (getBuild(), the engine
    // contract) at placement, not the load-start snapshot. clearGroups is no longer called up front for
    // a drawable build, so the current frame stays on screen (no flicker) until the new one is ready.
    var mySeq = ++S.reposeSeq;
    var build = getBuild();
    var chain = computeChain(build, S.pool, {});
    S.lastChain = chain;
    S.available = chain.available;

    if (!build || !chain.available) {
      clearGroups();
      showUnavailable(build, chain);
      return;
    }

    // Load every part in parallel, cache by url, then place. Only a NEW url is
    // fetched (spec 4.9): loadGLB is cached.
    // [PDP LENGTH 2026-09-22] The url list is gathered BEFORE the status is set,
    // so a quiet re-pose can ask allCached whether this pose costs any network.
    var urls = [];
    var i;
    for (i = 0; i < chain.placements.length; i++) {
      var u = chain.placements[i].model_glb;
      if (hasModel(u) && urls.indexOf(u) < 0) { urls.push(u); }
    }

    // [ROUND 2 fix 2] Keep the loading text up until the FIRST rendered frame of
    // this pose; the frame loop hides it once groups are on screen.
    if (!(opts.quiet && allCached(urls, S.glbCache))) { setStatus(S.settings.loading); }
    S.awaitingFrame = true;
    if (S.canvas) { S.canvas.style.display = 'block'; }
    if (S.presetsEl) { S.presetsEl.style.display = 'flex'; }

    Promise.all(urls.map(loadGLB)).then(function () {
      if (!S.visible || S.disposed || S.gen !== gen || !S.renderer) { return; }
      // [ROUND 5] Only the NEWEST repose places; a stale one drops WITHOUT touching the status/ring,
      // so it can never hide the newest pose's loading ring or leave a spinner behind (the newest pose
      // owns the ring: its frame draw hides it, or its own not-available/error path clears it).
      if (reposeSuperseded(mySeq, S.reposeSeq)) { return; }
      // Re-read the build at placement time (getBuild(), the engine contract) and recompute the chain.
      var freshBuild = getBuild();
      var freshChain = computeChain(freshBuild, S.pool, {});
      if (!freshBuild || !freshChain.available) {
        clearGroups(); S.lastChain = freshChain; S.available = false; showUnavailable(freshBuild, freshChain); return;
      }
      // If the fresh chain needs a url this load did not fetch (a build change landed in the rare
      // window after the snapshot but before the debounced repose fired), defer: the pending repose
      // will load and place it. Never place a partial chain.
      var j;
      for (j = 0; j < freshChain.placements.length; j++) {
        var fu = freshChain.placements[j].model_glb;
        if (hasModel(fu) && !S.glbCache[fu]) { return; }
      }
      S.lastChain = freshChain; S.available = true;
      // [PDP LENGTH 2026-09-22] placeChain -> clearGroups nulls S.contentH_in; a
      // keepCamera re-pose never calls fitCamera, so carry it across or a later
      // resize would recompute the canvas height from nothing.
      var keptContentH = S.contentH_in;
      placeChain(freshChain, freshBuild);   // placeChain clears the previous groups itself, then places
      S.loadedUrls = urls.slice();
      // [ROUND 4] Render the callouts FIRST so their real offsetHeight is available
      // when fitCamera -> applyCanvasSize computes the height cap, then fit.
      updateCallouts(freshBuild, freshChain, true);
      updateAria(freshBuild, freshChain);
      if (opts.keepCamera) { S.contentH_in = keptContentH; }
      else { fitCamera(); }
    }).catch(function (e) {
      if (S.gen !== gen || !S.visible || reposeSuperseded(mySeq, S.reposeSeq)) { return; }
      // [ROUND 2 fix 3] A GLB fetch/parse failure degrades to the same "not
      // available" presentation as an uncovered build, plus ONE console.warn (a
      // warn not an error, so the smoke's zero-console-error rule still holds).
      S.awaitingFrame = false;
      showUnavailable(build, chain);
      if (S.container) { S.container.setAttribute('data-vmscfg-3d-error', String(e && e.message ? e.message : e)); }
      try { console.warn('[vms-3d] ' + (e && e.message ? e.message : 'GLB load failed')); } catch (w) {}
    });
  }

  function loadGLB(url) {
    if (S.glbCache[url]) { return Promise.resolve(S.glbCache[url]); }
    if (S.glbPromises[url]) { return S.glbPromises[url]; }
    S.glbPromises[url] = new Promise(function (resolve, reject) {
      S.loader.load(url, function (gltf) { S.glbCache[url] = gltf.scene; resolve(gltf.scene); }, undefined,
        function (err) {
          // Do not cache the rejection: a later re-pose may retry the url.
          delete S.glbPromises[url];
          reject(new Error('GLB load failed: ' + url + (err && err.message ? ' (' + err.message + ')' : '')));
        });
    });
    return S.glbPromises[url];
  }

  function placeChain(chain, build) {
    var THREE = S.THREE;
    // [ROUND 5 REPOSE-RACE FIX 2026-09-20] Clear the previous chain's groups + TV HERE, immediately
    // before placing, so a stacked-repose double placement is impossible by construction (owner item D):
    // even if two placeChain calls ever ran, the second clears the first's objects before adding its own.
    clearGroups();
    S.tris = 0;
    S.assertions = {};
    var i;
    for (i = 0; i < chain.placements.length; i++) {
      var plc = chain.placements[i];
      var src = S.glbCache[plc.model_glb];
      if (!src) { continue; }
      // scene.clone(true) shares geometry (spec 4.3).
      var clone = src.clone(true);
      var group = new THREE.Group();
      group.add(clone);
      S.root.add(group);
      group.updateMatrixWorld(true);

      // Telescoping: translate tube_inner BEFORE reading anchors (spec 4.4).
      if (plc.role === 'pipe_tele') {
        var inner = clone.getObjectByName('tube_inner');
        if (inner) {
          var defaultY = inner.position.y; // the node's translation as loaded
          inner.position.y = defaultY - (plc.tele_solved_in - plc.length_min_in) * IN_TO_M;
          group.updateMatrixWorld(true);
        }
      }

      // Place by anchor_top: move the group so anchor_top lands at (0, y_top, 0).
      // Never use mesh bounds for part positions (spec 6.5 / 4.3).
      var aTop = clone.getObjectByName('anchor_top');
      if (aTop) {
        var wp = new THREE.Vector3();
        aTop.getWorldPosition(wp);
        group.position.set(0 - wp.x, plc.y_top_in * IN_TO_M - wp.y, 0 - wp.z);
        group.updateMatrixWorld(true);
      }

      // Assertions (spec 4.3, 4.4): head vesa_center, tele anchor_bottom.
      if (plc.role === 'head') {
        var vc = clone.getObjectByName('vesa_center');
        if (vc) {
          var vw = new THREE.Vector3(); vc.getWorldPosition(vw);
          S.assertions.head_vesa_delta_in = vw.y / IN_TO_M - chain.vesa_y_in;
          S.assertions.head_vesa_ok = near(vw.y / IN_TO_M, chain.vesa_y_in, ASSERT_TOL_IN);
        }
      }
      if (plc.role === 'pipe_tele') {
        var ab = clone.getObjectByName('anchor_bottom');
        if (ab) {
          var aw = new THREE.Vector3(); ab.getWorldPosition(aw);
          S.assertions.tele_bottom_delta_in = aw.y / IN_TO_M - plc.y_bottom_in;
          S.assertions.tele_bottom_ok = near(aw.y / IN_TO_M, plc.y_bottom_in, ASSERT_TOL_IN);
        }
      }

      countTris(group);
      S.groups.push(group);
    }

    // The TV box (spec 4.5). No simulated ceiling (owner 2026-09-16, superseding
    // spec 4.6): the dimension overlay from the contact line communicates it.
    addTVBox(chain, build);
    // Remember the two chain points (contact plane at y = 0, vesa center) for the
    // overlay; chainPoints.top stays at (0,0,0), the ceiling contact line.
    S.chainPoints = { top: new THREE.Vector3(0, 0, 0), bottom: new THREE.Vector3(0, chain.vesa_y_in * IN_TO_M, 0) };
    // [UX R2 2026-09-21] The parts changed, so the labels are rebuilt from the
    // chain that was just placed (proposals item 4).
    buildPartLabels(chain);
  }

  function countTris(obj) {
    obj.traverse(function (o) {
      if (o.isMesh && o.geometry) {
        var g = o.geometry;
        if (g.index) { S.tris += g.index.count / 3; }
        else if (g.attributes && g.attributes.position) { S.tris += g.attributes.position.count / 3; }
      }
    });
  }

  function headFrontZ() {
    // The ONE allowed mesh-bounds read (spec 4.5): the head's front extent (+Z),
    // used only for the cosmetic TV back-face offset.
    var THREE = S.THREE;
    var z = 0;
    // The head is the last group placed; +Z is its front (spec 4.5).
    if (S.groups.length) {
      var box = new THREE.Box3().setFromObject(S.groups[S.groups.length - 1]);
      if (isFinite(box.max.z)) { z = box.max.z; }
    }
    return z;
  }

  // [DD-HEAD 2026-09-22] THE TV BOX AS PURE NUMBERS, inches, so the pose is
  // testable without WebGL (scripts/test_vms_configurator_3d.js). Returns null
  // when there is nothing to draw. A vertical head is exactly the box addTVBox
  // always built: sceneModel's cabinet width x cabinet height x TV_DEPTH_IN, centred
  // on the VESA point, its back on the head's front face (backZ), the lighter
  // screen material on +Z (BoxGeometry material index 4). A HORIZONTAL (face-down)
  // head holds the TV flat under the disc.
  // [RULE, owner ruling 2026-09-22] "The side of the DD head seen in the
  // configurator orthographic is the short side of the TV." So the front view (the
  // same view as the 2D orthographic, where the slab is c wide) shows the TV's SHORT
  // side: X (left to right) = the cabinet HEIGHT c (VMSElevation.tvCabinetHeight,
  // 16.69 in for 32 in), Z (front to back) = the cabinet WIDTH
  // (VMSElevation.tvCabinetWidth, 29.67 in for 32 in), Y = HORIZONTAL_TV_THICKNESS
  // 2.5. Centred under the mast axis (x 0, z 0, not offset by the head front), its
  // TOP face on the disc (y = vesa_y - 2.5 / 2), the lighter screen material on -Y
  // (material index 3), facing the floor. The Side preset shows the long side.
  function tvBoxSpec(build, vesaY, backZ) {
    var E = window.VMSElevation;
    if (!E || typeof E.sceneModel !== 'function' || !build) { return null; }
    var head = build.head || {};
    var plate = build.plate || {};
    var y = num(vesaY);
    if (y === null) { return null; }
    var sm;
    try {
      sm = E.sceneModel({
        tvSize: build.tv_size_in,
        plateOffset: num(plate.adds_length_in),
        headOffset: num(head.adds_length_in),
        horizontal: !!head.horizontal
      });
    } catch (e) { return null; }
    if (!sm) { return null; }
    // Placed TV (SPEC-THEME-tv-placement-2026-10-12.md 3.2; notes assets/vms-tv-lookup.notes.md).
    var tp = build.tv_placement, pw = tp ? num(tp.width_in) : null, ph = tp ? num(tp.height_in) : null, pd = tp ? num(tp.depth_in) : null;
    var real = pw !== null && ph !== null && pd !== null && pw > 0 && ph > 0 && pd > 0;
    if (sm.horizontal) {
      // Face-down (spec 5.3): height along X, shifted toward top_edge_side; top face on the disc.
      if (real) { return { horizontal: true, w: ph, h: pd, d: pw, x: (tp.top_edge_side === 'left' ? -1 : tp.top_edge_side === 'right' ? 1 : 0) * (num(tp.holes_below_middle_in) || 0), y: y - pd / 2, z: 0, screenFace: 3 }; }
      var thick = num(E.HORIZONTAL_TV_THICKNESS);
      var cw = typeof E.tvCabinetWidth === 'function' ? num(E.tvCabinetWidth(build.tv_size_in)) : null;
      var ch = typeof E.tvCabinetHeight === 'function' ? num(E.tvCabinetHeight(build.tv_size_in)) : null;
      if (thick === null || cw === null || ch === null) { return null; }
      return { horizontal: true, w: ch, h: thick, d: cw, x: 0, y: y - thick / 2, z: 0, screenFace: 3 };
    }
    var bz = num(backZ) === null ? 0 : num(backZ);
    if (real) {
      return { horizontal: false, w: pw, h: ph, d: pd, x: 0, y: y + (num(tp.holes_below_middle_in) || 0), z: bz + pd / 2, screenFace: 4 };
    }
    var tvW = num(sm.tvWidth), tvH = num(sm.tvHeight);
    if (tvW === null || tvH === null) { return null; }
    return { horizontal: false, w: tvW, h: tvH, d: TV_DEPTH_IN, x: 0, y: y, z: bz + TV_DEPTH_IN / 2, screenFace: 4 };
  }

  function addTVBox(chain, build) {
    var THREE = S.THREE;
    // Dimensions from the elevation model (spec 4.5), through the pure tvBoxSpec.
    // headFrontZ() is in metres; the spec works in inches.
    var spec = tvBoxSpec(build, chain.vesa_y_in, headFrontZ() / IN_TO_M);
    if (!spec) { return; }

    // [ROUND 3 2026-09-16] Draw the TV translucent so the mount reads through it
    // (owner request). TV_OPACITY is the 0 to 1 fill fraction from the section
    // setting; at 1.0 the box is opaque and depth-writes exactly as before, below
    // 1.0 it is transparent with depthWrite off, at 0 only the outline draws.
    var TV_OPACITY = (S.settings && typeof S.settings.tvOpacity === 'number') ? S.settings.tvOpacity : 0.30;
    var tvTransparent = TV_OPACITY < 1;
    var geo = new THREE.BoxGeometry(spec.w * IN_TO_M, spec.h * IN_TO_M, spec.d * IN_TO_M);
    var matBody = new THREE.MeshStandardMaterial({ color: 0x3a3a3f, roughness: 0.85, metalness: 0.0, transparent: tvTransparent, opacity: TV_OPACITY, depthWrite: !tvTransparent });
    var matFront = new THREE.MeshStandardMaterial({ color: 0x55555b, roughness: 0.8, metalness: 0.0, transparent: tvTransparent, opacity: TV_OPACITY, depthWrite: !tvTransparent });
    // BoxGeometry face order: px, nx, py, ny, pz(front), nz. Lighter screen face:
    // pz (index 4) on a vertical head, ny (index 3, facing the floor) on a
    // face-down head.
    var mats = [matBody, matBody, matBody, matBody, matBody, matBody];
    mats[spec.screenFace] = matFront;
    var tv = new THREE.Mesh(geo, mats);
    tv.position.set(spec.x * IN_TO_M, spec.y * IN_TO_M, spec.z * IN_TO_M);
    tv.renderOrder = 2; // [ROUND 4c] kept for any future transparent object; no other transparent object exists after the ceiling removal. The outline child keeps its own default.
    // The screen silhouette as a line outline, so it still reads when the fill is
    // faint or invisible. It is a child of the TV mesh (same transform); its
    // EdgesGeometry and LineBasicMaterial are disposed with the mesh (disposeObject
    // handles isLine as well as isMesh). LineSegments is NOT isMesh, so countTris
    // leaves the outline out of S.tris (it counts triangles only).
    var edges = new THREE.LineSegments(
      new THREE.EdgesGeometry(geo),
      new THREE.LineBasicMaterial({ color: 0x2a2a2f, transparent: true, opacity: 0.7 })
    );
    tv.add(edges);
    S.root.add(tv);
    S.tvMesh = tv;
    S.tvFrontMat = matFront;
    countTris(tv);
  }

  // [ROUND 4 2026-09-16] The simulated ceiling helper and the drop-ceiling tile
  // plane were removed on owner instruction ("Get rid of the simulated ceiling"),
  // superseding spec 4.6. The dimension overlay from the y = 0 contact line down to
  // the VESA center now carries that meaning.

  // ---- camera framing -----------------------------------------------------
  function contentBox() {
    var THREE = S.THREE;
    var box = new THREE.Box3();
    box.makeEmpty();
    for (var i = 0; i < S.groups.length; i++) { box.expandByObject(S.groups[i]); }
    if (S.tvMesh) { box.expandByObject(S.tvMesh); }
    return box;
  }

  // [PDP LENGTH 2026-09-22, director round 2] THE ONE BOX EVERY FIT PATH READS.
  // contentBox() is the box of what is drawn right now; fitBox() is that box
  // extended DOWNWARD by the host's getFitExtraIn inches. The chain hangs from
  // y = 0 and a longer telescoping pipe only lowers everything under it by the
  // same amount, so the extended box IS the box of the longest pose this host can
  // reach without a re-pose. fitCamera and setView (the per view corner fit, which
  // the ResizeObserver path also runs through refitCurrentView) both read it, so a
  // resize can never re-frame to the short pose and undo the framing.
  // state()'s cornersMaxNdc deliberately keeps reading the REAL contentBox: it
  // reports whether what is DRAWN is inside the frustum, which is the check.
  function fitBox() {
    var box = contentBox();
    if (box.isEmpty()) { return box; }
    var extra = fitExtraIn(S.optGetFitExtraIn);
    if (extra > 0) { box.min.y -= extra * IN_TO_M; }
    return box;
  }

  var _fitDist = 1;
  function fitCamera() {
    var THREE = S.THREE;
    var box = fitBox();
    if (box.isEmpty()) { return; }
    var center = new THREE.Vector3(); box.getCenter(center);
    var size = new THREE.Vector3(); box.getSize(size);
    // [ROUND 4] Remember the content's VERTICAL extent (inches) so the canvas height
    // can grow with the build (canvasHeight uses it against a fixed reference width).
    // Apply the content-driven canvas size BEFORE the per-view fit, so the fit sees
    // the final aspect.
    S.contentH_in = size.y / IN_TO_M;
    S._center = center;
    S._minTargetY = (box.min.y / IN_TO_M - 10) * IN_TO_M; // no pan below the content bottom minus 10 in
    applyCanvasSize();
    // Seed a fit-distance estimate from maxDim; setView refines it per view by
    // projecting the 8 content-box corners.
    var maxDim = Math.max(size.x, size.y, size.z);
    var fov = S.camera.fov * Math.PI / 180;
    _fitDist = (maxDim / 2) / Math.tan(fov / 2) * 1.25 + maxDim * 0.15;
    S.controls.target.copy(center);
    setView(S.curAz, S.curEl); // default three-quarter (curAz 35, curEl 15) unless a preset set it
  }

  // Place the camera along the (dir) unit direction at distance d from the center.
  function placeCameraAt(dx, dy, dz, d) {
    S.camera.position.set(S._center.x + d * dx, S._center.y + d * dy, S._center.z + d * dz);
    S.camera.lookAt(S._center);
    S.camera.updateMatrixWorld(true);   // Camera.updateMatrixWorld also refreshes matrixWorldInverse
    S.camera.updateProjectionMatrix();
  }

  // Max over the 8 content-box corners of max(|ndc.x|, |ndc.y|) for the CURRENT
  // camera. 1.0 is the frustum edge; > 1 means a corner is off screen (cropping).
  function maxCornerNdc(box) {
    var THREE = S.THREE;
    if (!box || box.isEmpty() || !S.camera) { return 0; }
    var xs = [box.min.x, box.max.x], ys = [box.min.y, box.max.y], zs = [box.min.z, box.max.z];
    var v = new THREE.Vector3();
    var m = 0, a, b, c, ax, ay;
    for (a = 0; a < 2; a++) {
      for (b = 0; b < 2; b++) {
        for (c = 0; c < 2; c++) {
          v.set(xs[a], ys[b], zs[c]).project(S.camera);
          ax = Math.abs(v.x); ay = Math.abs(v.y);
          if (ax > m) { m = ax; }
          if (ay > m) { m = ay; }
        }
      }
    }
    return m;
  }

  function setView(azimuthDeg, elevationDeg) {
    if (!S._center) { return; }
    S.curAz = azimuthDeg; S.curEl = elevationDeg;
    var az = azimuthDeg * Math.PI / 180, el = elevationDeg * Math.PI / 180;
    var dx = Math.cos(el) * Math.sin(az);
    var dy = Math.sin(el);
    var dz = Math.cos(el) * Math.cos(az);
    // [ROUND 4] Fit by projected corners, per view, so Front / Side / three-quarter
    // each get their own distance and nothing crops in any preset. Start at the
    // maxDim seed, then iterate up to 4 times: place, measure the worst corner m,
    // grow if m > 0.92, gently shrink if m < 0.80, never below half the seed.
    // (The smoke's 0.97 crop limit is slack for the case where 4 iterations do not
    // fully converge; it is NOT a second target band, the target here is 0.80 to 0.92.)
    // [PDP LENGTH 2026-09-22, director round 2] fitBox, not contentBox: the SAME
    // extended box fitCamera used, so a refit from the ResizeObserver or a Front /
    // Side preset frames the longest reachable pose too.
    var box = fitBox();
    var d = _fitDist;
    var initial = _fitDist;
    var iter, m;
    for (iter = 0; iter < 4; iter++) {
      placeCameraAt(dx, dy, dz, d);
      m = maxCornerNdc(box);
      if (m > 0.92) { d = d * (m / 0.92); }
      else if (m < 0.80) {
        var shrunk = d * (m / 0.88);
        if (shrunk < 0.5 * initial) { shrunk = 0.5 * initial; }
        d = shrunk;
      } else { break; }
    }
    placeCameraAt(dx, dy, dz, d); // final placement at the fitted distance
    // OrbitControls tracks the fitted distance; target stays the content center.
    S.controls.target.copy(S._center);
    S.controls.minDistance = 0.5 * d;
    S.controls.maxDistance = 3 * d;
    S.controls.update();
  }

  // Re-fit the current view (used after a resize or re-pose).
  function refitCurrentView() { if (S._center) { setView(S.curAz, S.curEl); } }

  // ---- the render loop and the dimension overlay --------------------------
  function startLoop() {
    function frame() {
      if (S.disposed) { return; }
      S.animId = requestAnimationFrame(frame);
      if (S.controls) {
        // Clamp the pan target so it cannot drop below the TV bottom minus 10 in.
        if (S._minTargetY !== undefined && S.controls.target.y < S._minTargetY) { S.controls.target.y = S._minTargetY; }
        S.controls.update();
      }
      if (S.renderer && S.scene && S.camera) {
        S.renderer.render(S.scene, S.camera);
        // [ROUND 2 fix 2] First frame with parts on screen: drop the loading text.
        if (S.awaitingFrame && S.groups.length) {
          S.awaitingFrame = false;
          hideStatus();
          if (S.firstFrameAt === null) {
            S.firstFrameAt = (typeof performance !== 'undefined' && performance.now) ? performance.now() : Date.now();
          }
          // [ROUND 4] Mobile align-on-open, now that the canvas has grown to its
          // content height and the page is tall enough to scroll the container up.
          if (S.pendingOpenScroll) { S.pendingOpenScroll = false; scrollCanvasToTopIfMobile(); }
          // [UX R1 2026-09-21] FIRST FINISHED FRAME is the hint's cue: the ring is
          // down (hideStatus above) and parts are on screen. It decides once.
          maybeShowRotateHint();
        }
        updateDimOverlay();
        updatePartLabels();   // [UX R2 2026-09-21] proposals item 4
      }
    }
    S.animId = requestAnimationFrame(frame);
  }

  // [OVERLAY SPACE FIX 2026-09-22] The overlay elements this feeds (the dimension
  // line S.dimEl and the per part labels) are absolutely positioned inside
  // S.container, but the projection is measured against the CANVAS rect. The two
  // agree ONLY while the canvas starts at the container's top. On the PDP modal
  // the status paragraph stayed in normal flow above the canvas (see the [hidden]
  // rule in assets/vms-configurator-3d.css), so the canvas started about 60 px
  // down and the dimension line was painted that far too high: its top missed the
  // ceiling plate and its bottom missed the VESA centre. Return CONTAINER-space
  // coordinates by adding the canvas offset inside the container. That offset is
  // 0 on the configurator page and the slanted page (the canvas is the first laid
  // out child there), so nothing moves where the overlay was already right.
  function project(v) {
    var THREE = S.THREE;
    var p = v.clone().project(S.camera);
    var rect = S.canvas.getBoundingClientRect();
    var x = (p.x * 0.5 + 0.5) * rect.width;
    var y = (-p.y * 0.5 + 0.5) * rect.height;
    if (S.container && S.container.getBoundingClientRect) {
      var crect = S.container.getBoundingClientRect();
      x = x + (rect.left - crect.left);
      y = y + (rect.top - crect.top);
    }
    return { x: x, y: y };
  }

  function updateDimOverlay() {
    if (!S.dimEl || !S.chainPoints || !S.available) { return; }
    if (S.orbiting) { return; } // hidden while orbiting, back on release
    var top = project(S.chainPoints.top);
    var bot = project(S.chainPoints.bottom);
    var y0 = Math.min(top.y, bot.y), y1 = Math.max(top.y, bot.y);
    S.dimEl.classList.remove('is-hidden');
    S.dimLineEl.style.top = y0 + 'px';
    S.dimLineEl.style.height = (y1 - y0) + 'px';
    S.dimLabelEl.style.top = ((y0 + y1) / 2 - 8) + 'px';
    if (S.lastChain && S.lastChain.finished_in !== null) {
      S.dimLabelEl.textContent = S.settings.finished + ' ' + fmtIn(S.lastChain.finished_in);
    }
  }

  // ---- callouts and aria --------------------------------------------------
  function fmtIn(v) {
    // One decimal, the same shape the elevation callouts use (FACTS 2: fmt()).
    return (Math.round(v * 10) / 10).toFixed(1) + ' in';
  }

  function mastText(build, chain) {
    var out = [];
    for (var i = 0; i < chain.placements.length; i++) {
      var p = chain.placements[i];
      if (p.role === 'pipe_fixed') { out.push(p.sku + ' ' + fmtIn(p.y_top_in - p.y_bottom_in)); }
      else if (p.role === 'pipe_tele') { out.push(p.sku + ' ' + fmtIn(p.tele_solved_in) + ' (adjustable)'); }
    }
    return out.length ? out.join(', ') : '-';
  }

  function calloutRow(label, value) {
    var row = document.createElement('span');
    row.className = 'vmscfg-3d__callout';
    var l = document.createElement('span'); l.className = 'vmscfg-3d__callout-label'; l.textContent = label + ':';
    var v = document.createElement('span'); v.className = 'vmscfg-3d__callout-value'; v.textContent = value;
    row.appendChild(l); row.appendChild(v);
    return row;
  }

  function finishedFigure(build, chain) {
    // The same figure the 2D scene shows (spec 5): finished_length_in for a
    // fixed card, else the committed length_in.
    if (chain && chain.finished_in !== null && chain.finished_in !== undefined) { return fmtIn(chain.finished_in); }
    var t = num(build && build.length_in);
    return t !== null ? fmtIn(t) : '-';
  }

  function updateCallouts(build, chain, available) {
    if (!S.calloutsEl) { return; }
    S.calloutsEl.textContent = '';
    S.calloutsEl.appendChild(calloutRow(S.settings.finished, finishedFigure(build, chain)));
    S.calloutsEl.appendChild(calloutRow(S.settings.mast, available ? mastText(build, chain) : '-'));
    var tv = num(build && build.tv_size_in);
    // [PDP 3D VARIANT 2026-09-20] hideTvCallout (the PDP modal draws no TV) drops the TV row entirely.
    if (!S.hideTvCallout) {
      S.calloutsEl.appendChild(calloutRow(S.settings.tv, tv !== null ? fmtIn(tv).replace(' in', ' in diagonal') : '-'));
    }
    S.calloutsMeasured = true; // [ROUND 4c] the strip now has real content; offsetHeight is valid
  }

  function updateAria(build, chain) {
    if (!S.canvas) { return; }
    var tv = num(build && build.tv_size_in);
    var label = '3D view of the build: ' + S.settings.finished.toLowerCase() + ' ' + finishedFigure(build, chain);
    label += ', ' + S.settings.mast.toLowerCase() + ' ' + mastText(build, chain);
    if (tv !== null) { label += ', ' + S.settings.tv.toLowerCase() + ' ' + fmtIn(tv).replace(' in', ' in'); }
    // [UX R1 2026-09-21] The rotate hint itself is aria-hidden, so the instruction
    // is carried here IN WORDS on the canvas's own accessible name. Gated on the
    // same scope flag as the hint, so an injected (PDP modal) init keeps exactly
    // the label it has today.
    if (!S.rotateHintSuppressed && S.settings.rotate) { label += '. ' + S.settings.rotate; }
    S.canvas.setAttribute('aria-label', label);
  }

  // ---- resize -------------------------------------------------------------
  function onResize() {
    if (!S.renderer || !S.camera) { return; }
    // [ROUND 4] Re-apply the content-driven canvas size, then re-fit the current
    // view to the new aspect so nothing crops after a width or viewport change.
    // [ROUND 4c] Setting the canvas height changes the container's own height, which
    // re-fires the ResizeObserver; that bounce terminates after one pass because
    // canvasHeight is pure in (w, contentH_in, viewportH, calloutsH) and none of
    // those depend on the container's height, so the second pass computes the same
    // size and stops.
    applyCanvasSize();
    refitCurrentView();
  }

  // ---- disposal -----------------------------------------------------------
  function clearGroups() {
    if (!S.root) { return; }
    var THREE = S.THREE;
    var i;
    for (i = 0; i < S.groups.length; i++) { disposeObject(S.groups[i]); S.root.remove(S.groups[i]); }
    S.groups = [];
    if (S.tvMesh) { disposeObject(S.tvMesh); S.root.remove(S.tvMesh); S.tvMesh = null; S.tvFrontMat = null; }
    S.tris = 0;
    S.contentH_in = null; // [ROUND 4] content extent cleared with the groups
    S.calloutsMeasured = false; // [ROUND 4c] this pose has not rendered the strip yet
  }

  function disposeObject(obj) {
    // Dispose geometries and materials of CLONES; the cached source GLB scenes
    // (S.glbCache) keep their geometry for the page's life (spec 4.10 / 6.9).
    obj.traverse(function (o) {
      // [ROUND 3] Dispose meshes AND line objects: the TV outline is a
      // THREE.LineSegments child of the TV mesh, so its EdgesGeometry and
      // LineBasicMaterial must be released here too.
      if (o.isMesh || o.isLine) {
        if (o.geometry && o.geometry.dispose) { o.geometry.dispose(); }
        if (o.material) {
          if (Array.isArray(o.material)) { o.material.forEach(function (m) { if (m && m.dispose) { m.dispose(); } }); }
          else if (o.material.dispose) { o.material.dispose(); }
        }
      }
    });
  }

  function disposeGL() {
    S.disposed = true;
    if (S.animId) { cancelAnimationFrame(S.animId); S.animId = 0; }
    clearGroups();
    if (S.ro) { try { S.ro.disconnect(); } catch (e) {} S.ro = null; }
    if (S.controls && S.controls.dispose) { try { S.controls.dispose(); } catch (e2) {} }
    // [STUDIO ENV 2026-09-22] The prefiltered environment belongs to this renderer,
    // so it goes with it: drop the scene reference first, then dispose the texture.
    // The next buildRenderer builds a fresh one (never a second PMREM per renderer).
    if (S.scene) { try { S.scene.environment = null; } catch (eEnv) {} }
    if (S.envTex) { try { S.envTex.dispose(); } catch (eEnv2) {} }
    S.envTex = null; S.envMs = null;
    if (S.renderer) {
      try { S.renderer.dispose(); } catch (e3) {}
      if (S.canvas && S.canvas.parentNode) { S.canvas.parentNode.removeChild(S.canvas); }
    }
    if (S.presetsEl && S.presetsEl.parentNode) { S.presetsEl.parentNode.removeChild(S.presetsEl); }
    if (S.dimEl && S.dimEl.parentNode) { S.dimEl.parentNode.removeChild(S.dimEl); }
    // [UX R1 2026-09-21] Closing the view counts as the hint having had its turn,
    // so it is dismissed (and remembered) BEFORE the node goes. S.rotateHint is
    // deliberately NOT reset here: a close and reopen must not bring it back.
    dismissRotateHint();
    if (S.hintEl && S.hintEl.parentNode) { S.hintEl.parentNode.removeChild(S.hintEl); }
    // [UX R2 2026-09-21] The per part labels go with the canvas they were pinned to.
    if (S.partsEl && S.partsEl.parentNode) { S.partsEl.parentNode.removeChild(S.partsEl); }
    if (S.calloutsEl && S.calloutsEl.parentNode) { S.calloutsEl.parentNode.removeChild(S.calloutsEl); }
    S.renderer = null; S.scene = null; S.camera = null; S.controls = null; S.canvas = null;
    S.root = null; S.presetsEl = null; S.dimEl = null; S.hintEl = null; S.calloutsEl = null; S.built = false;
    S.partsEl = null; S.partLabels = []; S.presetsBox = null; S.presetsBoxW = -1; S.topLeftBox = null;
    S.chainPoints = null; S._center = null; S.awaitingFrame = false; S.pendingOpenScroll = false;
  }

  function dispose() { disposeGL(); }

  // ---- state() for the smoke test -----------------------------------------
  // World-space bbox max y in inches (ROUND 3, still used for the plate check).
  function bboxMaxY_in(obj) {
    if (!obj || !S.THREE) { return null; }
    try {
      var box = new S.THREE.Box3().setFromObject(obj);
      if (!isFinite(box.max.y)) { return null; }
      return box.max.y / IN_TO_M;
    } catch (e) { return null; }
  }

  // [ROUND 4] hasCeiling: a genuine runtime check that no mesh sits above the
  // contact plane (a ceiling object would have topped out well above y = 0). The
  // plate flange lives at or below y = 0, so this reads false now.
  function anyMeshAboveY_in(threshold_in) {
    if (!S.root || !S.THREE) { return false; }
    var found = false;
    var thr = threshold_in * IN_TO_M;
    S.root.traverse(function (o) {
      if (found || !o.isMesh || !o.geometry) { return; }
      var b = new S.THREE.Box3().setFromObject(o);
      if (isFinite(b.max.y) && b.max.y > thr) { found = true; }
    });
    return found;
  }

  // [ROUND 4] The worst projected corner for the CURRENT camera (see maxCornerNdc);
  // the smoke asserts this stays under the crop threshold for every build and view.
  function currentCornersMaxNdc() {
    if (!S.camera || !S.THREE) { return null; }
    var box = contentBox();
    if (box.isEmpty()) { return null; }
    S.camera.updateMatrixWorld(true);
    return maxCornerNdc(box);
  }

  function state() {
    return {
      // gen/hasRenderer/awaitingFrame expose the show/hide machine (ROUND 2).
      gen: S.gen, hasRenderer: !!S.renderer, awaitingFrame: S.awaitingFrame,
      visible: S.visible, disposed: S.disposed, built: S.built, bundleReady: S.bundleReady,
      available: S.available, loadedParts: S.loadedUrls.slice(), triangleCount: Math.round(S.tris),
      firstFrameAt: S.firstFrameAt, showClickAt: S.showClickAt, lastChain: S.lastChain,
      assertions: S.assertions,
      cardIndex: (S.lastChain ? S.lastChain.card_index : null),
      finishedDelta: (S.lastChain ? S.lastChain.finished_delta_in : null),
      // [ROUND 3] plate placement and TV translucency.
      plateMaxY_in: bboxMaxY_in(S.groups && S.groups.length ? S.groups[0] : null),
      tvOpacity: (S.settings && typeof S.settings.tvOpacity === 'number') ? S.settings.tvOpacity : null,
      tvTransparent: (S.tvFrontMat ? !!S.tvFrontMat.transparent : null),
      // [ROUND 4] no ceiling, the growing/capped canvas, and the crop check.
      hasCeiling: anyMeshAboveY_in(0.25),
      canvasH_in_px: (typeof S.appliedCanvasH === 'number' ? S.appliedCanvasH : null),
      calloutsH_px: (S.calloutsEl ? S.calloutsEl.offsetHeight : null),
      cornersMaxNdc: currentCornersMaxNdc(),
      // [ROUND 5 REPOSE-RACE FIX 2026-09-20] additive counts for the stacked-repose smoke: the placed
      // chain groups and the total root children (= groups + the one TV box). A clean pose draws exactly
      // one object per chain placement plus one TV; a stacked race would show a multiple.
      groupsCount: (S.groups ? S.groups.length : 0),
      rootChildCount: (S.root ? S.root.children.length : 0),
      // [UX R1 2026-09-21] ADDITIVE key for the hint: 'idle' until the first
      // finished frame decides, then 'shown', 'dismissed' or 'suppressed'.
      rotateHint: S.rotateHint,
      // [UX R2 2026-09-21] ADDITIVE key for the per part labels: the string
      // 'suppressed' on the injected (PDP variant modal) path, otherwise the
      // NUMBER of labels currently drawn.
      partLabels: (S.partLabelsSuppressed ? 'suppressed' : S.partLabels.length),
      // [STUDIO ENV 2026-09-22] ADDITIVE keys: whether the prefiltered studio
      // environment is actually on the scene, and the PMREM build cost in ms.
      studioEnv: !!(S.scene && S.scene.environment),
      studioEnvMs: S.envMs,
      // [DD-HEAD 2026-09-22] ADDITIVE key: the drawn TV box read back off the
      // mesh, in inches (geometry parameters and world position), or null.
      tvBox: tvBoxRead()
    };
  }

  function tvBoxRead() {
    var m = S.tvMesh;
    if (!m || !m.geometry || !m.geometry.parameters) { return null; }
    var p = m.geometry.parameters;
    var r = function (v) { return Math.round((v / IN_TO_M) * 10000) / 10000; };
    // Position in the chain frame (S.root local, where the anchor chain is laid
    // out in inches), plus the world position for reference.
    var lp = m.position;
    var wp = m.position.clone();
    if (m.parent && typeof m.getWorldPosition === 'function') { m.getWorldPosition(wp); }
    var screenFace = -1;
    if (Array.isArray(m.material)) {
      for (var i = 0; i < m.material.length; i++) { if (m.material[i] === S.tvFrontMat) { screenFace = i; } }
    }
    return { w: r(p.width), h: r(p.height), d: r(p.depth), x: r(lp.x), y: r(lp.y), z: r(lp.z),
      worldY: r(wp.y), screenFace: screenFace };
  }

  // ---- expose + boot ------------------------------------------------------
  window.VMSConfigurator3D = {
    computeChain: computeChain, parseTvOpacity: parseTvOpacity, canvasHeight: canvasHeight,
    reposeSuperseded: reposeSuperseded,
    // [UX R1 2026-09-21] pure hint helpers, exposed for scripts/test_vms_configurator_3d.js.
    rotateHintNext: rotateHintNext, rotateHintSeen: rotateHintSeen, rememberRotateHint: rememberRotateHint,
    // [UX R2 2026-09-21] pure per part label helpers, exposed for the same suite.
    partLabelEntries: partLabelEntries, layoutPartLabels: layoutPartLabels,
    // [PDP LENGTH 2026-09-22] the pure cache guard, and a host-driven re-pose.
    // repose(opts) runs today's repose at the CURRENT generation and accepts
    // { keepCamera: true, quiet: true }; with no opts it is exactly today's pose.
    allCached: allCached, fitExtraIn: fitExtraIn,
    // [DD-HEAD 2026-09-22] the pure TV box pose, for the same suite.
    tvBoxSpec: tvBoxSpec,
    repose: function (opts) { repose(S.gen, opts || {}); },
    init: init, show: show, hide: hide, dispose: dispose, state: state
  };

  function boot() {
    var wrap = document.querySelector('.vmscfg-3d-wrap');
    if (wrap) { init(wrap.querySelector('#elev3d') || wrap); }
  }
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', boot); }
  else { boot(); }
})();
